import React, { useContext, useEffect, useRef, useState } from "react";
import {
  CircularProgress,
  InputBase,
  makeStyles,
  Popover,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TICKET_STATUS_LABEL, formatListTime } from "./chatUtils";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    paper: {
      width: 380,
      maxWidth: "calc(100vw - 24px)",
      borderRadius: 12,
      color: theme.palette.text.primary,
      display: "flex",
      flexDirection: "column",
    },
    head: {
      padding: theme.spacing(1.5, 1.5, 1),
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    title: { fontSize: 14, fontWeight: 700 },
    search: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      height: 38,
      padding: theme.spacing(0, 1.25),
      borderRadius: 8,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#F8FAFC",
      "&:focus-within": { borderColor: theme.palette.primary.main },
    },
    label: {
      padding: theme.spacing(0.5, 1.5),
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: 0.4,
      color: theme.palette.text.secondary,
    },
    list: {
      maxHeight: 300,
      overflowY: "auto",
      paddingBottom: 6,
      ...theme.scrollbarStylesSoft,
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      padding: theme.spacing(1, 1.5),
      border: 0,
      background: "transparent",
      fontFamily: "inherit",
      textAlign: "left",
      cursor: "pointer",
      color: theme.palette.text.primary,
      "&:hover": { backgroundColor: theme.palette.action.hover },
    },
    itemActive: {
      backgroundColor: isDark ? "rgba(45,123,232,0.18)" : "#E8F0FC",
    },
    avatar: {
      width: 34,
      height: 34,
      borderRadius: "50%",
      objectFit: "cover",
      flexShrink: 0,
      backgroundColor: theme.palette.action.selected,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.secondary,
    },
    text: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
    name: {
      fontSize: 13,
      fontWeight: 600,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    meta: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    time: { fontSize: 11, color: theme.palette.text.secondary, flexShrink: 0 },
    empty: {
      padding: theme.spacing(3, 2),
      textAlign: "center",
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
  };
});

// Seletor "Citar atendimento": sem busca mostra os atendimentos abertos da
// pessoa; com busca, procura por nome/número do contato ou mensagem (mesma
// consulta da busca geral, que já respeita as filas do usuário).
export default function TicketPicker({ anchorEl, open, onClose, onSelect }) {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const inputRef = useRef();
  const [query, setQuery] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const term = query.trim();
    setLoading(true);
    const timer = setTimeout(async () => {
      const queueIds = (user?.queues || []).map((q) => q.id);
      const params = term
        ? {
            searchParam: term.toLowerCase(),
            pageNumber: 1,
            showAll: "true",
            queueIds: JSON.stringify(queueIds),
          }
        : {
            pageNumber: 1,
            status: "open",
            showAll: "false",
            queueIds: JSON.stringify(queueIds),
          };
      try {
        const { data } = await api.get("/tickets", { params });
        if (!cancelled) setTickets((data?.tickets || []).slice(0, 20));
      } catch (err) {
        if (!cancelled) setTickets([]);
      }
      if (!cancelled) {
        setLoading(false);
        setActive(0);
      }
    }, term ? 300 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, open, user]);

  const choose = (ticket) => {
    if (!ticket) return;
    onSelect(ticket);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, tickets.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(tickets[active]);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      classes={{ paper: classes.paper }}
      TransitionProps={{ onEntered: () => inputRef.current && inputRef.current.focus() }}
    >
      <div className={classes.head}>
        <span className={classes.title}>Citar atendimento</span>
        <label className={classes.search}>
          <SearchIcon style={{ fontSize: 18, opacity: 0.6 }} />
          <InputBase
            inputRef={inputRef}
            placeholder="Nome ou número do contato"
            inputProps={{ "aria-label": "Buscar atendimento" }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            style={{ flex: 1, fontSize: 13 }}
          />
          {loading && <CircularProgress size={16} />}
        </label>
      </div>
      <span className={classes.label}>
        {query.trim() ? "Resultados" : "Meus atendimentos abertos"}
      </span>
      <div className={classes.list} role="listbox" aria-label="Atendimentos">
        {!loading && tickets.length === 0 && (
          <div className={classes.empty}>
            {query.trim()
              ? "Nenhum atendimento encontrado."
              : "Você não tem atendimentos abertos. Busque pelo nome do contato."}
          </div>
        )}
        {tickets.map((t, i) => (
          <button
            type="button"
            key={t.id}
            role="option"
            aria-selected={i === active}
            className={`${classes.item} ${i === active ? classes.itemActive : ""}`}
            onMouseEnter={() => setActive(i)}
            onClick={() => choose(t)}
          >
            {t.contact?.profilePicUrl ? (
              <img src={t.contact.profilePicUrl} alt="" className={classes.avatar} />
            ) : (
              <span className={classes.avatar}>
                {(t.contact?.name || "?").charAt(0).toUpperCase()}
              </span>
            )}
            <span className={classes.text}>
              <span className={classes.name}>{t.contact?.name || "Contato"}</span>
              <span className={classes.meta}>
                {[
                  `#${t.id}`,
                  t.queue?.name,
                  TICKET_STATUS_LABEL[t.status] || t.status,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <span className={classes.time}>{formatListTime(t.updatedAt)}</span>
          </button>
        ))}
      </div>
    </Popover>
  );
}
